import { supabase } from '../supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { SupervisorShift } from '@shiftly/shared';
import { DateTime } from 'luxon';
import { convertDateFields } from './supabaseSupervisorShiftService';
import { useAuth } from '../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';

export interface RealtimeSupervisorShiftManagerOptions {
  enabled?: boolean;
  onError?: (error: any) => void;
}

export const useRealtimeSupervisorShiftManager = (options: RealtimeSupervisorShiftManagerOptions = {}) => {
  const { enabled = true, onError } = options;
  const { user, permissions, session, loading } = useAuth();
  const query_client = useQueryClient();
  const subscription_ref = useRef<any>(null);

  // Only enable if auth is ready and user is authenticated
  const should_enable = enabled && !loading && !!user && !!session && permissions.includes(Permissions.SUPERVISOR_SHIFTS_VIEW);

  const updateSupervisorShiftQueries = useCallback((payload: any) => {
    const { eventType: event_type, new: new_record, old: old_record } = payload;

    // Get all supervisor shift-related queries
    const supervisor_shift_queries = query_client.getQueriesData<SupervisorShift[]>({
      queryKey: ['supabase-supervisor-shifts']
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Supabase realtime supervisor shift update:', { eventType: event_type, recordId: new_record?.id || old_record?.id });
    }

    supervisor_shift_queries.forEach(([query_key, old_data]) => {
      if (!Array.isArray(old_data)) return;

      query_client.setQueryData(query_key, (current_data: SupervisorShift[] | undefined) => {
        if (!current_data) return current_data;

        switch (event_type) {
          case 'INSERT': {
            const shift = convertDateFields(new_record);
            // Check if this supervisor shift should be included in this query
            if (shouldIncludeSupervisorShiftInQuery(shift, query_key)) {
              const exists = current_data.some(existingShift => existingShift.id === shift.id);
              if (!exists) {
                return [...current_data, shift].sort((a, b) =>
                  a.start_date_time.toMillis() - b.start_date_time.toMillis()
                );
              }
            }
            return current_data;
          }

          case 'UPDATE': {
            const shift = convertDateFields(new_record);
            if (shouldIncludeSupervisorShiftInQuery(shift, query_key)) {
              // Update existing or add new
              const existing_index = current_data.findIndex(existingShift => existingShift.id === shift.id);
              if (existing_index >= 0) {
                const new_data = [...current_data];
                new_data[existing_index] = shift;
                return new_data.sort((a, b) => a.start_date_time.toMillis() - b.start_date_time.toMillis());
              } else {
                return [...current_data, shift].sort((a, b) =>
                  a.start_date_time.toMillis() - b.start_date_time.toMillis()
                );
              }
            } else {
              // Remove if no longer matches query criteria
              return current_data.filter(existingShift => existingShift.id !== shift.id);
            }
          }

          case 'DELETE': {
            return current_data.filter(shift => shift.id !== old_record.id);
          }

          default:
            return current_data;
        }
      });
    });

    // Also invalidate related queries like user stats
    if (new_record?.user_id || old_record?.user_id) {
      const user_id = new_record?.user_id || old_record?.user_id;
      query_client.invalidateQueries({
        queryKey: ['supabase-user-period-stats', user_id],
        refetchType: 'none'
      });
    }
  }, [query_client]);

  const shouldIncludeSupervisorShiftInQuery = (supervisor_shift: SupervisorShift, query_key: readonly unknown[]): boolean => {
    // Extract query parameters from the query key
    const query_params = query_key[1] as any;
    if (!query_params?.filters) return true;

    // Check if supervisor shift matches all filters
    return query_params.filters.every((filter: any) => {
      const { field, operator, value } = filter;
      const shift_value = getNestedValue(supervisor_shift, field);

      switch (operator) {
        case 'eq':
          return shift_value === value;
        case 'neq':
          return shift_value !== value;
        case 'gt':
          if (DateTime.isDateTime(shift_value)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shift_value.toMillis() > compareValue.toMillis();
          }
          return shift_value > value;
        case 'gte':
          if (DateTime.isDateTime(shift_value)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shift_value.toMillis() >= compareValue.toMillis();
          }
          return shift_value >= value;
        case 'lt':
          if (DateTime.isDateTime(shift_value)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shift_value.toMillis() < compareValue.toMillis();
          }
          return shift_value < value;
        case 'lte':
          if (DateTime.isDateTime(shift_value)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shift_value.toMillis() <= compareValue.toMillis();
          }
          return shift_value <= value;
        case 'in':
          return Array.isArray(value) && value.includes(shift_value);
        case 'not-in':
          return Array.isArray(value) && !value.includes(shift_value);
        default:
          return true;
      }
    });
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  useEffect(() => {
    if (!should_enable) return;

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (subscription_ref.current) subscription_ref.current.unsubscribe();
        subscription_ref.current = supabase
          .channel('realtime supervisor shift subscription', { config: { private: true } })
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'supervisor_shifts',
            },
            (payload) => {
              updateSupervisorShiftQueries(payload);
            }
          )
          .subscribe((status, err) => {
            console.log('Realtime supervisor shift subscription status:', status);
            if (err && onError) {
              onError(err);
            }
          });
      } else {
        console.log('User signed out, cleaning up realtime supervisor shift subscription');
        if (subscription_ref.current) {
          subscription_ref.current.unsubscribe();
          subscription_ref.current = null;
        }
      }
    });

    return () => {
      console.log('Cleaning up realtime supervisor shift subscription');
      listener?.subscription.unsubscribe();
      if (subscription_ref.current) {
        subscription_ref.current.unsubscribe();
        subscription_ref.current = null;
      }
    };
  }, [should_enable, updateSupervisorShiftQueries, onError]);

  return {
    isConnected: !!subscription_ref.current
  };
};